import { Conference } from "@easycv/common/types/conference";
import {
  ContextConstants,
  ContextMethods,
  ContextState
} from "@easycv/common/types/context";
import { User } from "@easycv/common/types/user";
import { Backend } from "@easycv/common/types/util";
import dayjs from "dayjs";
import {
  getPeriodFromDateObject,
  isTimeWithinRange
} from "../../lib/dateHelpers.js";
import { escapeHtml } from "../../lib/frontendUtilities.js";
import { getPathsBreakdown } from "../../lib/hierarchyUtilities.js";
import { getLastPosition } from "../../lib/reportUtilities.js";
import { CCInteractiveTable } from "../cclib/CCInteractiveTable";
import { CCManage } from "../cclib/CCManage";
import { CCSetup } from "../cclib/CCSetup";
import { CCStructure } from "../cclib/CCStructure";
import { CCStyling, DefaultStyling } from "../cclib/CCStyling";
import { getDivisionChoices } from "../cclib/CCUtility";
import { CCHeader, CCText, CCWrapper } from "../cclib/components/Form";
import { CCIcon } from "../cclib/components/UI";
import {
  CCInteractiveTableColumn,
  CCInteractiveTableEditability
} from "../cclib/types/table";
import {
  adminCustomTableSections,
  teachingCustomTableSections,
  uvaMedAnnualReviewStructure
} from "./structure_uva_med_annual_review";

const dataPrefix = "uva_med_annual_review_";
const disabledEditability: CCInteractiveTableEditability = {
  allowRemovingRows: false,
  allowEditingText: false,
  allowAddingRows: false,
  disableEverything: true
};

// The key here is the type of the entry in the database, the value is what we are mapping it to for uva
// This is just how I decided to write this part, nothing to do with cclib.
// Defined here so it can be shared by the summary update and the detail update
// Defined as a function that returns this object for state reasons.
//  (Count n entries, change year, entries wont be reset to 0, need fresh object)
const getPapersTypes = () => ({
  "Research Investigations": { uvaKey: "Peer Reviewed", count: 0 },
  chapters: { uvaKey: "Book Chapters", count: 0 },
  books: { uvaKey: "Books", count: 0 },
  nonprint: { uvaKey: "Health Ed/Videos etc", count: 0 },
  short: { uvaKey: "Short Communications", count: 0 },
  abstracts: { uvaKey: "Abstracts", count: 0 }
});
// These types are the same in the db as how uva wants them so its a simpler mapping
const getLecturesTypes = () => ({
  "Grand Rounds": 0,
  "Invited Lecture": 0,
  Paper: 0,
  Platform: 0,
  Poster: 0,
  Symposium: 0,
  Workshop: 0,
  Custom: 0
});

const tableRowsSorter = (sortOnKey: string, rows: any[]) =>
  rows.sort(
    (x: { [key: string]: string }, y: { [key: string]: string }): number => {
      const xUpper = (x[sortOnKey] ?? "").toUpperCase();
      const yUpper = (y[sortOnKey] ?? "").toUpperCase();
      return xUpper < yUpper ? -1 : xUpper > yUpper ? 1 : 0;
    }
  );

// These "set data" parts are in the update block so that they can change when the view year changes.
const papersSummaryUpdate = (data: any, cc: CCSetup) => {
  const papersSummaryTypes = getPapersTypes();
  const scholarship = cc.newDef.lists?.scholarship ?? {};
  for (const key of Object.keys(scholarship)) {
    if (!Boolean(scholarship[key])) continue;
    if (isTimeWithinRange(scholarship[key].time, cc.startDate, cc.endDate)) {
      // Dont do anything with data we arent expecting
      if (papersSummaryTypes[scholarship[key].type] != undefined) {
        ++papersSummaryTypes[scholarship[key].type].count;
      }
    }
  }
  (data as CCInteractiveTable).clear();
  (data as CCInteractiveTable).bulkAddRowsFromData(
    Object.keys(papersSummaryTypes).map((key) => ({
      type: papersSummaryTypes[key].uvaKey,
      count: String(papersSummaryTypes[key].count)
    }))
  );
};
const papersDetailUpdate = (data: any, cc: CCSetup) => {
  const papersDetailTypes = getPapersTypes();
  const rows = [];
  const scholarship = cc.newDef.lists?.scholarship ?? {};
  for (const key of Object.keys(scholarship)) {
    if (!Boolean(scholarship[key])) continue;
    if (isTimeWithinRange(scholarship[key].time, cc.startDate, cc.endDate)) {
      // Dont do anything with data we arent expecting
      if (papersDetailTypes[scholarship[key].type] != undefined) {
        // Pushing to rows instead of directly to the table so we can sort by type
        rows.push({
          type: papersDetailTypes[scholarship[key].type].uvaKey,
          date: getPeriodFromDateObject(scholarship[key]),
          citation: scholarship[key].line,
          comments: scholarship[key].comments
        });
      }
    }
  }
  tableRowsSorter("type", rows);
  (data as CCInteractiveTable).clear();
  (data as CCInteractiveTable).bulkAddRowsFromData(rows);
};

const lecturesSummaryUpdate = (data: any, cc: CCSetup) => {
  const lecturesSummaryTypes = getLecturesTypes();
  const presentations = cc.newDef.lists?.presentations ?? {};
  for (const key of Object.keys(presentations)) {
    if (!Boolean(presentations[key])) continue;
    if (isTimeWithinRange(presentations[key].time, cc.startDate, cc.endDate)) {
      if (lecturesSummaryTypes[presentations[key].presType] != undefined) {
        ++lecturesSummaryTypes[presentations[key].presType];
      } else {
        // Else it's a count for custom
        ++lecturesSummaryTypes["Custom"];
      }
    }
  }
  (data as CCInteractiveTable).clear();
  (data as CCInteractiveTable).bulkAddRowsFromData(
    Object.keys(lecturesSummaryTypes).map((key) => ({
      type: key,
      count: String(lecturesSummaryTypes[key])
    }))
  );
};
const lecturesDetailUpdate = (data: any, cc: CCSetup) => {
  const lecturesDetailTypes = getLecturesTypes();
  const rows = [];
  const presentations = cc.newDef.lists?.presentations ?? {};
  for (const key of Object.keys(presentations)) {
    if (!Boolean(presentations[key])) continue;
    if (isTimeWithinRange(presentations[key].time, cc.startDate, cc.endDate)) {
      // Dont do anything with data we arent expecting
      if (lecturesDetailTypes[presentations[key].presType] != undefined) {
        // Pushing to rows instead of directly to the table so we can sort by type
        rows.push({
          type: presentations[key].presType,
          date: getPeriodFromDateObject(presentations[key]),
          title: presentations[key].title,
          location: presentations[key].location,
          custom: ""
        });
      } else {
        // Else its a custom entry
        rows.push({
          type: "Custom",
          date: getPeriodFromDateObject(presentations[key]),
          title: presentations[key].title,
          location: presentations[key].location,
          custom: presentations[key].presType
        });
      }
    }
  }
  tableRowsSorter("type", rows);
  (data as CCInteractiveTable).clear();
  (data as CCInteractiveTable).bulkAddRowsFromData(rows);
};

const papersSummarySection = (container: JQuery, cc: CCSetup) => {
  container.append(
    CCWrapper(CCHeader("h5", "", "Papers Published or in Press (Summary)"))
  );
  const papersSummaryColumns: CCInteractiveTableColumn[] = [
    { display: "Type", db: "type" },
    { display: "Count", db: "count" }
  ];

  const papersSummaryTable = new CCInteractiveTable(
    container,
    cc,
    "papersSummary",
    dataPrefix,
    "custom-papers-summary",
    papersSummaryColumns,
    { editable: disabledEditability }
  );
  return papersSummaryTable;
};
const papersDetailSection = (container: JQuery, cc: CCSetup) => {
  container.append(
    CCWrapper(CCHeader("h5", "", "Papers Published or in Press (Detail)"))
  );
  const papersDetailColumns: CCInteractiveTableColumn[] = [
    { display: "Type", db: "type" },
    { display: "Date", db: "date" },
    { display: "Citation", db: "citation" },
    { display: "Special Comments", db: "comments" }
  ];

  const papersDetailTable = new CCInteractiveTable(
    container,
    cc,
    "papersDetail",
    dataPrefix,
    "custom-papers-detail",
    papersDetailColumns,
    { editable: disabledEditability }
  );
  return papersDetailTable;
};

const lecturesSummarySection = (container: JQuery, cc: CCSetup) => {
  container.append(
    CCWrapper(CCHeader("h5", "", "Invited Lectures and Symposiums (Summary)"))
  );
  const lecturesSummaryColumns: CCInteractiveTableColumn[] = [
    { display: "Type", db: "type" },
    { display: "Count", db: "count" }
  ];

  const lecturesSummaryTable = new CCInteractiveTable(
    container,
    cc,
    "lecturesSummary",
    dataPrefix,
    "custom-lectures-summary",
    lecturesSummaryColumns,
    { editable: disabledEditability }
  );
  return lecturesSummaryTable;
};

const lecturesDetailSection = (container: JQuery, cc: CCSetup) => {
  container.append(
    CCWrapper(CCHeader("h5", "", "Invited Lectures and Symposiums (Detail)"))
  );
  const lecturesDetailColumns: CCInteractiveTableColumn[] = [
    { display: "Type", db: "type" },
    { display: "Date", db: "date" },
    { display: "Title", db: "title" },
    { display: "Location", db: "location" },
    { display: "Custom", db: "custom" }
  ];

  const lecturesDetailTable = new CCInteractiveTable(
    container,
    cc,
    "lecturesDetail",
    dataPrefix,
    "custom-lectures-detail",
    lecturesDetailColumns,
    { editable: disabledEditability }
  );
  return lecturesDetailTable;
};

const fundedSection = (container: JQuery, cc: CCSetup) => {
  container.append(
    CCWrapper(CCHeader("h5", "", "Funded Research Efforts (all activities)"))
  );
  const fundedTableColumns: CCInteractiveTableColumn[] = [
    { display: "Date", db: "date" },
    { display: "Source", db: "source" },
    { display: "Title", db: "title" },
    { display: "Role", db: "role" },
    { display: "Role Detail", db: "role_detail" },
    { display: "Pending (Y/N)", db: "pending" },
    { display: "Amount", db: "amount" }
  ];
  return new CCInteractiveTable(
    container,
    cc,
    "funded",
    dataPrefix,
    "custom-funded",
    fundedTableColumns,
    { editable: disabledEditability }
  );
};
const unfundedSection = (container: JQuery, cc: CCSetup) => {
  container.append(
    CCWrapper(CCHeader("h5", "", "Unfunded Research Efforts (all active)"))
  );

  const unfundedTableColumns: CCInteractiveTableColumn[] = [
    { display: "Date", db: "date" },
    { display: "Source", db: "source" },
    { display: "Title", db: "title" },
    { display: "Role", db: "role" },
    { display: "Role Detail", db: "role_detail" },
    { display: "Pending (Y/N)", db: "pending" }
  ];

  return new CCInteractiveTable(
    container,
    cc,
    "unfunded",
    dataPrefix,
    "custom-unfunded",
    unfundedTableColumns,
    { editable: disabledEditability }
  );
};
const fundedAndUnfundedUpdate = (data: any, cc: CCSetup) => {
  let fundedOrUnfundedUpdate =
    (data as CCInteractiveTable).baseClass === "unfunded";
  const projects = cc.newDef.lists?.projects ?? {};
  const rows = [];
  for (const key of Object.keys(projects)) {
    if (!Boolean(projects[key])) continue;
    if (Boolean(projects[key].isExplicitlyUnfunded) != fundedOrUnfundedUpdate) {
      continue;
    }
    if (isTimeWithinRange(projects[key].time, cc.startDate, cc.endDate)) {
      let roleDetail = "";
      if (projects[key].role === "Co-Principal Investigator") {
        roleDetail = "Co-PI Name: ";
      } else if (projects[key].role === "Multi-Principal Investigator") {
        roleDetail = "MPI Name: ";
      } else if (
        [
          "Site Principal Investigator",
          "Project Principal Investigator"
        ].includes(projects[key].role)
      ) {
        roleDetail = "Overall PI: ";
      } else if (projects[key].role !== "Principal Investigator") {
        roleDetail = "PI Name: ";
      }
      if (fundedOrUnfundedUpdate) {
        rows.push({
          date: getPeriodFromDateObject(projects[key]),
          source: projects[key].sourceType,
          title: projects[key].title,
          role: projects[key].role,
          role_detail:
            roleDetail === "" ? "" : roleDetail + projects[key].piName,
          pending: Boolean(projects[key].pending) ? "Y" : "N"
        });
      } else {
        rows.push({
          date: getPeriodFromDateObject(projects[key]),
          source: projects[key].sourceType,
          title: projects[key].title,
          role: projects[key].role,
          role_detail:
            roleDetail === "" ? "" : roleDetail + projects[key].piName,
          pending: Boolean(projects[key].pending) ? "Y" : "N",
          amount: projects[key].directCosts
        });
      }
    }
  }
  tableRowsSorter("source", rows);
  (data as CCInteractiveTable).clear();
  (data as CCInteractiveTable).bulkAddRowsFromData(rows);
};

const contributionSection = (container: JQuery, cc: CCSetup) => {
  container.append(
    CCWrapper(
      CCHeader(
        "h5",
        "",
        "Other Contributions (i.e. miscellaneous activites that enhance growth, quality, and visibilty of faculty member of department such as work at Free Clinic, tumor boards, work conferences. List titles and frequency)"
      )
    )
  );
  const columns: CCInteractiveTableColumn[] = [
    { display: "Date", db: "date" },
    { display: "Description", db: "description" },
    { display: "Frequency", db: "frequency" }
  ];
  return new CCInteractiveTable(
    container,
    cc,
    "contributionTable",
    dataPrefix,
    "custom-other-contribution",
    columns,
    {
      editable: {
        allowAddingRows: true,
        allowEditingText: true,
        allowRemovingRows: true,
        disableEverything: false
      },
      richTextEnabled: true
    }
  );
};
const contributionUpdate = (data: any, cc: CCSetup) => {
  const table = data as CCInteractiveTable;
  if (cc.yearData.status !== "mentee") {
    table.disableEditing();
  } else {
    table.enableEditing();
  }
  table.populateFromDatabase();
  table.refreshRichTextInstances();
};

const teachingSection = (container: JQuery, cc: CCSetup) => {
  let id = container.prop("id") as string;
  const key = id.substring(id.lastIndexOf("-") + 1);
  container.append(
    CCWrapper(CCHeader("h5", "", teachingCustomTableSections[key].text))
  );
  return new CCInteractiveTable(
    container,
    cc,
    `teaching-${key}`,
    dataPrefix,
    `custom-teaching-${key}`,
    teachingCustomTableSections[key].columns,
    { editable: disabledEditability }
  );
};
const teachingUpdate = (data: any, cc: CCSetup) => {
  const teachingType = (data as CCInteractiveTable).baseClass.split("-")[1];
  const rows = [];
  const teaching = cc.newDef.lists?.teaching ?? {};
  for (const key of Object.keys(teaching)) {
    if (!Boolean(teaching[key])) continue;
    if (teaching[key].type !== teachingCustomTableSections[teachingType].dbtype)
      continue;

    if (isTimeWithinRange(teaching[key].time, cc.startDate, cc.endDate)) {
      rows.push({
        date: getPeriodFromDateObject(teaching[key]),
        activity: teaching[key].title,
        location:
          teachingType == "classroom"
            ? teaching[key].workTime
            : teaching[key].location
      });
    }
  }
  tableRowsSorter("date", rows);
  (data as CCInteractiveTable).clear();
  (data as CCInteractiveTable).bulkAddRowsFromData(rows);
};

const adminSection = (container: JQuery, cc: CCSetup) => {
  let id = container.prop("id") as string;
  const key = id.substring(id.lastIndexOf("-") + 1);
  container.append(
    CCWrapper(CCHeader("h5", "", adminCustomTableSections[key].text))
  );
  return new CCInteractiveTable(
    container,
    cc,
    `admin-${key}`,
    dataPrefix,
    `custom-admin-${key}`,
    adminCustomTableSections[key].columns,
    { editable: disabledEditability }
  );
};
const adminUpdate = (data: any, cc: CCSetup) => {
  const adminType = (data as CCInteractiveTable).baseClass.split("-")[1];
  const rows = [];
  const admin = cc.newDef.lists?.committeeService ?? {};
  for (const key of Object.keys(admin)) {
    if (!Boolean(admin[key])) continue;
    if (admin[key].type !== adminCustomTableSections[adminType].dbtype)
      continue;
    if (isTimeWithinRange(admin[key].time, cc.startDate, cc.endDate)) {
      rows.push({
        date: getPeriodFromDateObject(admin[key]),
        name: admin[key].title,
        organization: admin[key].location
      });
    }
  }
  tableRowsSorter("date", rows);
  (data as CCInteractiveTable).clear();
  (data as CCInteractiveTable).bulkAddRowsFromData(rows);
};

const recognitionSection = (container: JQuery, cc: CCSetup) => {
  const columns: CCInteractiveTableColumn[] = [
    { display: "Date", db: "date" },
    { display: "Award Title", db: "title" },
    { display: "Institution", db: "institution" }
  ];
  return new CCInteractiveTable(
    container,
    cc,
    "recognitionTable",
    dataPrefix,
    "custom-recognition",
    columns,
    { editable: disabledEditability }
  );
};
const recognitionUpdate = (data: any, cc: CCSetup) => {
  const honors = cc.newDef.lists?.honors ?? {};
  const rows = [];
  for (const key of Object.keys(honors)) {
    if (!Boolean(honors[key])) continue;
    if (isTimeWithinRange(honors[key].time, cc.startDate, cc.endDate)) {
      rows.push({
        date: getPeriodFromDateObject(honors[key]),
        title: honors[key].title,
        institution: honors[key].location
      });
    }
  }
  tableRowsSorter("date", rows);
  (data as CCInteractiveTable).clear();
  (data as CCInteractiveTable).bulkAddRowsFromData(rows);
};

const formUpdate = (cc: CCSetup) => {
  // Try and guess the person's division, but let them change if we are wrong
  const thisYearData = cc.newDef.career[cc.viewYear].data;
  if (
    thisYearData[dataPrefix + "faculty_division"] == undefined ||
    thisYearData[dataPrefix + "faculty_division"] == "Choose"
  ) {
    const ownerBreakdown: [
      {
        root: string;
        department: string;
        divisions: string[];
      }
    ] = getPathsBreakdown(
      cc.contextConstants.cvOwner.paths,
      cc.contextConstants.hierarchy,
      cc.contextConstants.hierarchyNames
    );
    const cvOwnerDivisions = ownerBreakdown.flatMap(
      (entry) => entry?.divisions
    );

    const divOptions = getDivisionChoices(
      cc.conference,
      cc.contextConstants.hierarchy,
      cc.contextConstants.hierarchyNames
    );
    for (const div of cvOwnerDivisions) {
      if (!Boolean(div)) continue;
      if (divOptions.some((option) => option == div)) {
        cc.setValue("faculty_division", div);
        break;
      }
    }
  }

  if (!Boolean(thisYearData[dataPrefix + "research_activities_areas"])) {
    cc.setValue(
      "research_activities_areas",
      cc.newDef.researchInterests.description ?? ""
    );
  }
  if (!Boolean(thisYearData[dataPrefix + "research_activities_current"])) {
    cc.setValue(
      "research_activities_current",
      cc.newDef.researchInterests.current ?? ""
    );
  }
  if (!Boolean(thisYearData[dataPrefix + "research_activities_collab"])) {
    cc.setValue(
      "research_activities_collab",
      cc.newDef.researchInterests.collaboration ?? ""
    );
  }

  const priorYearData = cc.newDef.career[cc.viewYear - 1]?.data;
  if (priorYearData != undefined) {
    const carePriors = [
      "care_gross_rev",
      "care_visits",
      "care_work_rvus",
      "care_operations"
    ];
    for (const prior of carePriors) {
      // If this year's prior value is nothing
      // and last year's current value is something
      if (
        !Boolean(thisYearData[dataPrefix + prior + "_prior"]) &&
        Boolean(priorYearData[dataPrefix + prior])
      ) {
        cc.setValue(prior + "_prior", priorYearData[dataPrefix + prior]);
      }
    }

    cc.setValue("goals_prior", priorYearData[dataPrefix + "goals_coming"]);
  }
};

export default {
  title: "UVA Medicine Annual Review",
  tabTitle: "Annual Review",

  cvSetup: ({
    contextMethods,
    contextConstants,
    contextState,
    conference,
    backend
  }: {
    contextMethods: ContextMethods;
    contextConstants: ContextConstants;
    contextState: ContextState;
    conference: Conference;
    backend: Backend;
  }) => {
    const setup = new CCSetup(
      conference,
      backend,
      dataPrefix,
      contextMethods,
      contextConstants,
      contextState,
      formUpdate
    );

    // Figure out who my mentor is
    const cvOwnerId = contextConstants.cvOwner.id;
    const cvOwnersMentors: string[] = [];
    for (const mentor of Object.keys(conference.mentors)) {
      if (Boolean(conference.mentors[mentor][cvOwnerId])) {
        cvOwnersMentors.push(escapeHtml(contextConstants.mentors[mentor].name));
      }
    }

    setup.addButton(
      "default",
      CCIcon("arrow-left"),
      "header",
      "careerYearPrev",
      () => setup.changeYear(-1)
    );
    setup.addHTML(
      CCText("h4", "careerYear", setup.period, DefaultStyling.careerYear),
      {
        relative: "after",
        selector: ".careerYearPrev"
      }
    );
    setup.addButton(
      "default",
      CCIcon("arrow-right"),
      "header",
      "careerYearNext",
      () => setup.changeYear(1)
    );

    setup.addButton(
      "primary",
      CCIcon("download") + "<span>Date Limited CV</span>",
      "header",
      "downloadDateLimited",
      () => setup.dateLimitedCVExport(contextState.customExportSettings)
    );
    setup.addButton(
      "primary",
      CCIcon("download") + "<span>Full CV</span>",
      "header",
      "downloadFull",
      () => setup.fullCVExport(contextState.customExportSettings)
    );

    setup.addButton(
      "primary",
      CCIcon("download") + "<span>Download Review</span>",
      "header",
      "downloadDataSummary",
      () => {
        // This function is called right before the output is created
        const data = setup.newDef.career[setup.viewYear]?.data;
        const dataOverride = {};
        if (data != undefined) {
          dataOverride["percent_yes_explain"] =
            data[dataPrefix + "percent_yes_explain"] ?? "";
          const papersCounting =
            data[dataPrefix + "custom-papers-summary"] ?? {};
          for (const key of Object.keys(papersCounting)) {
            if (!Boolean(papersCounting[key])) continue;
            let export_type = "health";
            if (papersCounting[key].type != "Health Ed/Videos etc") {
              export_type = papersCounting[key].type
                .toLowerCase()
                .replaceAll(/ /g, "_");
            }
            dataOverride[`papers_${export_type}_count`] =
              papersCounting[key].count;
          }
          const lecturesCounting =
            data[dataPrefix + "custom-lectures-summary"] ?? {};
          for (const key of Object.keys(lecturesCounting)) {
            if (!Boolean(lecturesCounting[key])) continue;
            const export_type = lecturesCounting[key].type
              .toLowerCase()
              .replaceAll(/ /g, "_");
            dataOverride[`lectures_${export_type}_count`] =
              lecturesCounting[key].count;
          }
          dataOverride["eval_start"] = dayjs(setup.startDate).year();
          dataOverride["eval_end"] = dayjs(setup.endDate).year();
          if (data[dataPrefix + "cclib_finalized_timestamp"] != undefined) {
            dataOverride["finalized_timestamp"] = dayjs(
              data[dataPrefix + "cclib_finalized_timestamp"]
            ).format("MM/DD/YYYY");
          }
          dataOverride["evaluator"] = cvOwnersMentors[0];

          dataOverride["mentee_sign_sign"] =
            data[dataPrefix + "menteeSign"]?.signature;

          if (data[dataPrefix + "menteeSign"]?.time != undefined) {
            dataOverride["mentee_sign_time"] = dayjs(
              data[dataPrefix + "menteeSign"]?.time
            ).format("MM/DD/YYYY");
          }

          dataOverride["mentor_sign_sign"] =
            data[dataPrefix + "mentorSign"]?.signature;

          if (data[dataPrefix + "mentorSign"]?.time != undefined) {
            dataOverride["mentor_sign_time"] = dayjs(
              data[dataPrefix + "mentorSign"]?.time
            ).format("MM/DD/YYYY");
          }
        }
        setup.conferenceExport(
          "uva_med_annual_review_v1_1.docx",
          "Annual Review",
          dataOverride,
          () => "",
          {
            font: "Verdana",
            size: "11pt"
          }
        );
      }
    );
    setup.addButton(
      "primary",
      CCIcon("print") + "<span>Print</span>",
      "header",
      "downloadPrint",
      () => setup.printExport("Title")
    );

    const evaluatorsText = `Evaluator${cvOwnersMentors.length > 1 ? "s" : ""}: ${cvOwnersMentors.join(",")}`;

    const { hierarchy, hierarchyNames } = contextConstants;

    const struct = uvaMedAnnualReviewStructure;
    let divOptions: string[];
    if (struct[0]["sections"] != undefined) {
      for (const section of struct[0]["sections"]) {
        if (section.key == "faculty_division") {
          section.options = divOptions = getDivisionChoices(
            conference,
            hierarchy,
            hierarchyNames
          );
          break;
        }
      }
    }

    const structure = new CCStructure(struct);
    setup.generateFieldsFromStructure(structure, setup.ccRoot, false);

    setup.addHTML(
      CCWrapper(CCText("h4", "", evaluatorsText), "", DefaultStyling.note),
      {
        relative: "prepend",
        selector: "#printableContents"
      }
    );
    setup.addHTML(
      CCWrapper(
        CCHeader(
          "h4",
          "",
          "Information for this form is pulled directly from your CV. Anything created, updated, or deleted on this form is a localized change and does NOT flow back to the original CV record. Please bring your CV up-to-date before completing the form."
        ),
        "",
        CCStyling.join(DefaultStyling.header, DefaultStyling.inputText)
      ),
      {
        relative: "prepend",
        selector: "#printableContents"
      }
    );

    setup.defineCustomSection(
      "custom-papers-summary",
      papersSummarySection,
      papersSummaryUpdate
    );
    setup.defineCustomSection(
      "custom-lectures-summary",
      lecturesSummarySection,
      lecturesSummaryUpdate
    );
    setup.defineCustomSection(
      "custom-papers-detail",
      papersDetailSection,
      papersDetailUpdate
    );
    setup.defineCustomSection(
      "custom-lectures-detail",
      lecturesDetailSection,
      lecturesDetailUpdate
    );
    setup.defineCustomSection(
      "custom-funded",
      fundedSection,
      fundedAndUnfundedUpdate
    );
    setup.defineCustomSection(
      "custom-unfunded",
      unfundedSection,
      fundedAndUnfundedUpdate
    );

    for (const key of Object.keys(teachingCustomTableSections)) {
      setup.defineCustomSection(
        `custom-teaching-${key}`,
        teachingSection,
        teachingUpdate
      );
    }

    setup.defineCustomSection(
      "custom-other-contribution",
      contributionSection,
      contributionUpdate
    );

    for (const key of Object.keys(adminCustomTableSections)) {
      setup.defineCustomSection(
        `custom-admin-${key}`,
        adminSection,
        adminUpdate
      );
    }
    setup.defineCustomSection(
      "custom-recognition",
      recognitionSection,
      recognitionUpdate
    );

    const lastPos = getLastPosition(setup.newDef) ?? {};
    setup.populateReadonlyValues([
      {
        key: "faculty_name",
        value: setup.newDef?.bio?.name ?? "",
        yearAgnostic: true
      }
    ]);
    setup.setValue("faculty_rank", lastPos.title ?? "");
    setup.setValue("faculty_years_in_rank", lastPos?.years?.toFixed(1) ?? "");
    const conferenceBreakdown: [
      {
        root: string;
        department: string;
        divisions: string[];
      }
    ] = getPathsBreakdown(conference.paths, hierarchy, hierarchyNames);
    if (conferenceBreakdown.length && conferenceBreakdown[0].department) {
      setup.setValue(
        "faculty_department",
        conferenceBreakdown[0]?.department ?? "",
        true
      );
    }

    setup.appendStatusButtons();
    setup.update(setup.struct.structure);
    contextMethods.updateLayout();

    // Need to check that these percent fields add up to 100%
    const percentFields = ["research", "care", "teaching", "admin", "other"];
    const updatePercentFields = () => {
      const data = setup.newDef.career[setup.viewYear].data;
      const sum = percentFields.reduce((prev, curr) => {
        if (isNaN(prev)) return NaN;
        const value = parseFloat(data[dataPrefix + `percent_${curr}`]);
        return value == 0 ? NaN : prev + value;
      }, 0.0);
      setup.forceErrorState('[data-key*="percent"]:lt(5)', sum != 100);
    };
    updatePercentFields();
    for (const fieldKey of percentFields) {
      const element = $(`[data-key="percent_${fieldKey}"]`);
      element.off("input");
      element.on("input", (event) => {
        const target = $(event.target);
        const value = parseFloat(String(target.val() || ""));
        if (isNaN(value)) {
          setup.newDef.career[setup.viewYear].data[
            dataPrefix + `percent_${fieldKey}`
          ] = 0.0;
        } else {
          setup.newDef.career[setup.viewYear].data[
            dataPrefix + `percent_${fieldKey}`
          ] = value;
        }
        updatePercentFields();
        setup.backend.send(
          "log",
          `q - percent_${fieldKey} - String - ${value} - user ${(setup.user || {}).id}`
        );
        setup.checkSave();
      });
    }
  },
  manageSetup: (
    settingsEl: JQuery,
    { conference, updateConfDate, saveConference, hierarchyNames },
    user: User
  ) => {
    const ccManage = new CCManage(conference, saveConference, updateConfDate, {
      includeSections: {
        cutoff: true,
        mentees: true,
        goals: false,
        questions: false,
        mentors: true,
        reminders: false,
        years: true
      },
      sortBy: "mentor",
      verbage: {
        mentor: "Evaluator",
        mentee: "Mentee"
      }
    });
    ccManage.createSettingsElements(settingsEl);
    updateConfDate(conference);
    ccManage.createMentorsMenteesSection();
    ccManage.createAwesomplete();
    ccManage.hookMessageButton(user);
    ccManage.populateMenteesSection(hierarchyNames);
  }
};
